import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Buildings, PencilSimple, FirstAid, Target, User, Check, X, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useApi, errMsg } from "../lib/api";
import { GROUP_TYPES, COUNTRIES } from "../lib/constants";
import { useMe } from "../components/AppShell";

const ORDER = ["family", "company", "school", "hospital", "organisation"];
const ICONS = { family: Heart, company: Buildings, school: PencilSimple, hospital: FirstAid, organisation: Target };

const TypeCard = ({ k, active, onClick }) => {
  const t = GROUP_TYPES[k], I = ICONS[k];
  return (
    <button type="button" data-testid={`group-type-${k}`} aria-pressed={active} onClick={onClick}
      className={`flex flex-col items-start gap-3 p-5 rounded-[20px] text-left border-2 transition-colors lift ${active ? "bg-[#1C2F52] border-[#FF8F9C]" : "bg-[#162845] border-[#22385E] hover:border-slate-500"}`}>
      <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: t.tint, color: t.ink }}><I size={22} weight="bold" /></span>
      <span className="font-display font-extrabold text-lg">{t.label}</span>
      <span className="text-sm text-slate-300">{t.blurb}</span>
      <span className="space-y-1">{t.features.map((f) => <span key={f} className="flex items-center gap-2 text-xs text-slate-300"><Check size={12} weight="bold" style={{ color: t.ink }} />{f}</span>)}</span>
    </button>
  );
};

function StepType({ type, setType, onNext }) {
  return (<>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{ORDER.map((k) => <TypeCard key={k} k={k} active={type === k} onClick={() => setType(k)} />)}
      <button type="button" data-testid="group-type-solo" aria-pressed={type === "solo"} onClick={() => setType("solo")}
        className={`flex flex-col items-start gap-3 p-5 rounded-[20px] text-left border-2 border-dashed transition-colors ${type === "solo" ? "border-[#FF8F9C] bg-[#1C2F52]" : "border-[#22385E] hover:border-slate-500"}`}>
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-slate-800 text-teal-300"><User size={22} weight="bold" /></span>
        <span className="font-display font-extrabold text-lg">Just me</span><span className="text-sm text-slate-300">Solo plan – one profile, one or more tags.</span></button></div>
    <div className="flex flex-wrap items-center justify-between gap-3 mt-8"><div className="text-sm text-slate-300" data-testid="group-type-selected">Selected: <b>{type === "solo" ? "Just me" : GROUP_TYPES[type].label}</b></div>
      <Button data-testid="onboarding-continue-1" onClick={onNext} className="bg-red-600 hover:bg-red-500 text-white rounded-full px-8">Continue</Button></div>
  </>);
}

function StepDetails({ f, setF, onBack, onNext, busy }) {
  const t = GROUP_TYPES[f.type];
  const solo = f.type === "solo";
  return (
    <div className="panel p-6 max-w-2xl space-y-4">
      <label className="block space-y-1.5"><span className="text-sm text-slate-300">{solo ? "Name (optional)" : `${t.label} name`}</span>
        <Input data-testid="group-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={`[${solo ? "Your" : t.label} name]`} /></label>
      <label className="block space-y-1.5"><span className="text-sm text-slate-300">Country & currency</span>
        <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}><SelectTrigger data-testid="group-country-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(COUNTRIES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`country-${k}`}>{v}</SelectItem>)}</SelectContent></Select></label>
      <div className="flex justify-between pt-2"><Button variant="ghost" data-testid="onboarding-back-2" onClick={onBack}>Back</Button>
        <Button data-testid={solo ? "create-group-btn" : "onboarding-continue-2"} disabled={busy || (!solo && !f.name.trim())} onClick={onNext} className="bg-red-600 hover:bg-red-500 text-white rounded-full px-8">{solo ? "Create" : "Continue"}</Button></div>
    </div>
  );
}

function StepSubgroups({ f, setF, onBack, onCreate, busy }) {
  const t = GROUP_TYPES[f.type];
  const [v, setV] = useState("");
  const add = () => { const n = v.trim(); if (n && !f.subgroups.includes(n)) setF({ ...f, subgroups: [...f.subgroups, n] }); setV(""); };
  return (
    <div className="panel p-6 max-w-2xl space-y-4">
      <p className="text-sm text-slate-300">Add your {t.sub.toLowerCase()} now, or skip and add them later in Settings.</p>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <Input data-testid="subgroup-name-input" value={v} onChange={(e) => setV(e.target.value)} placeholder={`[${t.subOne} name]`} /><Button type="submit" variant="outline" data-testid="subgroup-add-btn">Add</Button></form>
      <div className="flex flex-wrap gap-2" data-testid="subgroup-chips">{f.subgroups.map((s) => (
        <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1 text-sm">{s}
          <button type="button" aria-label={`Remove ${s}`} onClick={() => setF({ ...f, subgroups: f.subgroups.filter((x) => x !== s) })}><X size={12} /></button></span>))}</div>
      <div className="flex justify-between pt-2"><Button variant="ghost" data-testid="onboarding-back-3" onClick={onBack}>Back</Button>
        <Button data-testid="create-group-btn" disabled={busy} onClick={onCreate} className="bg-red-600 hover:bg-red-500 text-white rounded-full px-8">{f.subgroups.length ? "Create group" : "Skip & create group"}</Button></div>
    </div>
  );
}

const TITLES = { 1: "Who is this group for?", 2: "Tell us about it", 3: "Set up your groups" };

export default function Onboarding() {
  const api = useApi();
  const nav = useNavigate();
  const { reload } = useMe();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ type: "school", name: "", country: "UG", subgroups: [] });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/groups", { ...f, name: f.name || (f.type === "solo" ? "My bsafe" : ""), subgroups: f.type === "solo" ? [] : f.subgroups });
      await reload();
      toast.success(r.data.trial ? "Group created · your free trial has started" : "Group created");
      nav(r.data.solo_person_id ? `/app/profile/${r.data.solo_person_id}` : `/app/g/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const title = step === 3 ? `Set up your ${GROUP_TYPES[f.type].sub.toLowerCase()}` : TITLES[step];
  return (
    <div data-testid="onboarding-page" className="max-w-5xl">
      <Link to="/app" data-testid="onboarding-back-dashboard" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6"><ArrowLeft size={14} />Back to dashboard</Link>
      <div className="overline" data-testid="onboarding-step">Step {step} of {f.type === "solo" ? 2 : 3}</div>
      <h1 className="text-4xl sm:text-5xl font-black mt-2">{title}</h1>
      {step === 1 && <p className="text-slate-400 mt-3 max-w-2xl mb-8">Every group gets a dashboard, NFC + QR cards or wristbands for each person, and a scan log. The labels and alerts adapt to your group.</p>}
      <div className={step === 1 ? "" : "mt-8"}>
        {step === 1 && <StepType type={f.type} setType={(type) => setF({ ...f, type })} onNext={() => setStep(2)} />}
        {step === 2 && <StepDetails f={f} setF={setF} busy={busy} onBack={() => setStep(1)} onNext={() => (f.type === "solo" ? submit() : setStep(3))} />}
        {step === 3 && <StepSubgroups f={f} setF={setF} busy={busy} onBack={() => setStep(2)} onCreate={submit} />}
      </div>
    </div>
  );
}
